import { supabase } from '@/integrations/supabase/client';

export interface LayoutPhoto {
  id: string;
  sequence_id: string;
  photo_url: string;
  storage_path: string;
  display_order: number;
  caption?: string;
  created_at: string;
  updated_at: string;
}

export interface LayoutPhotoUpload {
  file: File;
  caption?: string;
}

class LayoutPhotoService {
  /**
   * Get signed upload URL for layout photo
   */
  async getSignedUploadUrl(sequenceId: string, fileName: string, upsert: boolean = true): Promise<{
    path: string;
    signedUrl: string;
    token: string;
    uniqueFileName: string;
  }> {
    const user = await supabase.auth.getUser();
    if (!user.data.user) {
      throw new Error('User not authenticated');
    }

    const response = await fetch('/api/sequences/layout-photos/signed-upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sequenceId,
        userId: user.data.user.id,
        fileName,
        upsert,
      }),
    });

    if (!response.ok) {
      let errorMessage = `Failed to get signed upload URL (${response.status}: ${response.statusText})`;

      try {
        const error = await response.json();
        if (error.error) {
          errorMessage = error.error;
        }
      } catch (parseError) {
        // Ignore parsing errors, use basic message
      }

      console.error('Signed upload URL error:', {
        status: response.status,
        statusText: response.statusText,
        sequenceId,
        fileName,
        upsert
      });

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Upload photo to Supabase Storage using signed URL
   */
  async uploadPhoto(signedUrl: string, file: File): Promise<void> {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      let errorMessage = `Failed to upload photo (${response.status}: ${response.statusText})`;

      // Try to get more specific error details
      try {
        const errorData = await response.text();
        if (errorData) {
          errorMessage += ` - ${errorData}`;
        }
      } catch (parseError) {
        // Ignore parsing errors, use basic message
      }

      console.error('Photo upload error:', {
        status: response.status,
        statusText: response.statusText,
        url: signedUrl,
        fileSize: file.size,
        fileType: file.type,
        fileName: file.name
      });

      throw new Error(errorMessage);
    }
  }

  /**
   * Save layout photo metadata to database
   */
  async saveLayoutPhoto(
    sequenceId: string,
    storagePath: string,
    displayOrder: number,
    caption?: string
  ): Promise<LayoutPhoto> {
    // Get the public URL for the uploaded file
    const { data: urlData } = supabase.storage
      .from('display-assets')
      .getPublicUrl(storagePath);

    const { data, error } = await supabase
      .from('sequence_layout_photos')
      .insert({
        sequence_id: sequenceId,
        photo_url: urlData.publicUrl,
        storage_path: storagePath,
        display_order: displayOrder,
        caption,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to save layout photo: ${error.message}`);
    }

    return data;
  }

  /**
   * Get layout photos for a sequence
   */
  async getLayoutPhotos(sequenceId: string): Promise<LayoutPhoto[]> {
    const { data, error } = await supabase
      .from('sequence_layout_photos')
      .select('*')
      .eq('sequence_id', sequenceId)
      .order('display_order', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch layout photos: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Delete layout photo
   */
  async deleteLayoutPhoto(photoId: string): Promise<void> {
    // First, get the photo details to delete from storage
    const { data: photo, error: fetchError } = await supabase
      .from('sequence_layout_photos')
      .select('storage_path')
      .eq('id', photoId)
      .single();

    if (fetchError) {
      throw new Error(`Failed to fetch photo details: ${fetchError.message}`);
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('display-assets')
      .remove([photo.storage_path]);

    if (storageError) {
      console.error('Failed to delete from storage:', storageError);
      // Continue with database deletion even if storage deletion fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('sequence_layout_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      throw new Error(`Failed to delete layout photo: ${dbError.message}`);
    }
  }

  /**
   * Update photo caption
   */
  async updateCaption(photoId: string, caption: string): Promise<void> {
    const { error } = await supabase
      .from('sequence_layout_photos')
      .update({ caption })
      .eq('id', photoId);

    if (error) {
      throw new Error(`Failed to update caption: ${error.message}`);
    }
  }

  /**
   * Reorder photos
   */
  async reorderPhotos(photoIds: string[]): Promise<void> {
    const updates = photoIds.map((id, index) => ({
      id,
      display_order: index,
    }));

    for (const update of updates) {
      const { error } = await supabase
        .from('sequence_layout_photos')
        .update({ display_order: update.display_order })
        .eq('id', update.id);

      if (error) {
        throw new Error(`Failed to reorder photos: ${error.message}`);
      }
    }
  }

  /**
   * Upload multiple layout photos for a sequence
   */
  async uploadLayoutPhotos(
    sequenceId: string,
    photos: LayoutPhotoUpload[]
  ): Promise<LayoutPhoto[]> {
    const uploadedPhotos: LayoutPhoto[] = [];

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      try {
        // Get signed upload URL
        const uploadData = await this.getSignedUploadUrl(sequenceId, photo.file.name);

        // Upload to storage
        await this.uploadPhoto(uploadData.signedUrl, photo.file);

        // Save metadata
        const savedPhoto = await this.saveLayoutPhoto(
          sequenceId,
          uploadData.path,
          i,
          photo.caption
        );

        uploadedPhotos.push(savedPhoto);
      } catch (error) {
        console.error(`Failed to upload photo ${i + 1}:`, error);
        // Clean up any successfully uploaded photos
        for (const uploadedPhoto of uploadedPhotos) {
          try {
            await this.deleteLayoutPhoto(uploadedPhoto.id);
          } catch (cleanupError) {
            console.error('Failed to cleanup photo:', cleanupError);
          }
        }
        throw error;
      }
    }

    return uploadedPhotos;
  }
}

export const layoutPhotoService = new LayoutPhotoService();